import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaDownload,
  FaClock,
  FaMapMarkerAlt,
  FaArrowRight
} from 'react-icons/fa';
import ScrollReveal from './animations/ScrollReveal';
import SpotlightCard from './effects/SpotlightCard';
import MagneticButton from './effects/MagneticButton';
import NeuralField from './effects/NeuralField';
import CodeRain from './effects/CodeRain';
import { profile, mailto } from '../data/profile';

const channels = [
  {
    label: 'Email',
    value: profile.email,
    href: mailto(),
    icon: FaEnvelope,
    external: false
  },
  {
    label: 'LinkedIn',
    value: 'Connect with me',
    href: profile.social.linkedin,
    icon: FaLinkedin,
    external: true
  },
  {
    label: 'GitHub',
    value: 'See what I build',
    href: profile.social.github,
    icon: FaGithub,
    external: true
  },
  {
    label: 'X / Twitter',
    value: 'Occasional thoughts',
    href: profile.social.twitter,
    icon: FaTwitter,
    external: true
  }
];

export default function Contact() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="relative overflow-hidden bg-slate-50 py-section-sm sm:py-section dark:bg-slate-900"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <NeuralField opacity={0.4} density={28} />
        <CodeRain opacity={0.18} density={14} />
        <motion.div
          className="absolute left-1/2 top-1/3 h-[60vw] max-h-[520px] w-[60vw] max-w-[520px] -translate-x-1/2
                     rounded-full bg-emerald-500/10 blur-[130px]"
          animate={prefersReducedMotion ? undefined : { opacity: [0.4, 0.65, 0.4] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-5xl px-6">
        <ScrollReveal direction="up">
          <div className="text-center">
            <p className="eyebrow justify-center">Contact</p>

            <h2 id="contact-heading" className="section-title mt-4">
              Let’s build something <span className="text-gradient">great together.</span>
            </h2>

            <p className="section-lead mx-auto max-w-2xl">
              Have a project in mind, or just want a second opinion on one? Send me the details and
              I’ll come back with honest thoughts, even if I’m not the right person for it.
            </p>
          </div>
        </ScrollReveal>

        {/* Availability */}
        <ScrollReveal direction="up" delay={0.08}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm">
            <span className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-emerald-400" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              {profile.availability}
            </span>
            <span className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <FaClock className="h-3 w-3" aria-hidden="true" />
              {profile.responseTime}
            </span>
            <span className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <FaMapMarkerAlt className="h-3 w-3" aria-hidden="true" />
              {profile.location} &middot; {profile.timezone}
            </span>
          </div>
        </ScrollReveal>

        {/* Primary actions */}
        <ScrollReveal direction="up" delay={0.14}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <MagneticButton
              href={mailto()}
              className="group inline-flex items-center gap-2.5 rounded-full bg-slate-900 px-8 py-4 text-sm
                         font-semibold text-white shadow-lg shadow-slate-900/20 transition-shadow duration-300
                         hover:shadow-xl hover:shadow-blue-500/25 dark:bg-white dark:text-slate-900"
            >
              <FaEnvelope className="h-3.5 w-3.5" aria-hidden="true" />
              Send me an email
              <FaArrowRight
                className="h-2.5 w-2.5 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </MagneticButton>

            <MagneticButton
              href={profile.resume}
              download="Qasim-Afzaal-Resume.pdf"
              strength={0.2}
              className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white/70 px-8
                         py-4 text-sm font-semibold text-slate-800 backdrop-blur-md transition-colors duration-300
                         hover:border-blue-500/50 hover:text-blue-600 dark:border-white/10 dark:bg-white/[0.04]
                         dark:text-white dark:hover:text-blue-400"
            >
              <FaDownload className="h-3.5 w-3.5" aria-hidden="true" />
              Download resume
            </MagneticButton>
          </div>
        </ScrollReveal>

        {/* Channels */}
        <div className="mt-14 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {channels.map((channel, index) => {
            const Icon = channel.icon;
            return (
              <ScrollReveal
                key={channel.label}
                direction="up"
                delay={0.18 + index * 0.05}
                className="h-full"
              >
                <SpotlightCard className="h-full">
                  <a
                    href={channel.href}
                    {...(channel.external
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    className="flex h-full items-center gap-3.5 p-5"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-slate-100
                                 text-slate-600 transition-colors duration-300 group-hover:bg-blue-500/10
                                 group-hover:text-blue-500 dark:bg-white/[0.06] dark:text-slate-300"
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        {channel.label}
                      </span>
                      <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                        {channel.value}
                      </span>
                    </span>
                  </a>
                </SpotlightCard>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
